import { pluralCategory } from '@/i18n/format.ts'
import type { Locale } from '@/i18n/locale.ts'

/**
 * `home` — the root route, and the first thing anyone sees after signing in.
 *
 * English is the source of truth AND the type (PRD-27 LOC-011): `Messages = typeof en`, so
 * the Portuguese sibling is checked against the shape of this file and a missing key is a
 * compile error rather than a runtime fallback.
 *
 * Anything that interpolates is a function returning a whole sentence, because a translator
 * who is handed "Good " and ", " cannot produce a language whose greeting puts the name
 * first — or, as it turns out, one that has no word for a person whose name it does not know.
 *
 * The tile bodies each name the unit of the number above them. A bare mono count next to the
 * word "Knowledge" could mean bases, documents or passages, and a reader who guesses wrong
 * has been misinformed by a screen that was technically accurate.
 */

/** The slice of `useI18n()` a counting entry here needs, threaded in from the page. */
type Counting = { locale: Locale; formatCount: (value: number) => string }

/**
 * Picks the form CLDR says this count takes.
 *
 * Never `count === 1`: `Intl.PluralRules('pt-BR').select(0)` returns `one`, so Portuguese
 * takes the singular at zero and a two-branch comparison is already wrong in one of the two
 * shipped languages, on every empty state. `other` is required and is the fallback, so a
 * locale with `few`/`many` can add them here without touching a call site.
 */
const plural = (
  fmt: Counting,
  count: number,
  forms: Partial<Record<Intl.LDMLPluralRule, string>> & { other: string }
): string => forms[pluralCategory(fmt.locale, count)] ?? forms.other

export const home = {
  /**
   * The greeting, by the hour on the reader's own clock.
   *
   * `firstName` is `null` when the session has not named anybody — a display name is user
   * content and may simply be absent. English fills that hole with a word ("there"); most
   * languages have no such word, and the Portuguese sibling drops the address entirely
   * rather than transliterating one (PRD-27 D-21). Which is only possible because what
   * crosses this boundary is the absence itself and not a substitute noun already chosen
   * for it.
   */
  greeting: (hour: number, firstName: string | null): string => {
    const name = firstName ?? 'there'
    if (hour < 12) return `Good morning, ${name}`
    if (hour < 18) return `Good afternoon, ${name}`
    return `Good evening, ${name}`
  },
  standfirst: 'Ask anything. Every answer shows the passages it used, what it cost, and what it did.',

  composerLabel: 'Ask a question',
  composerPlaceholder: 'What do you want to know?',
  ask: 'Ask',
  asking: 'Starting…',

  tiles: {
    agents: { title: 'Agents', body: 'Assistants shared with you. Each one says what it knows.' },
    knowledge: { title: 'Knowledge', body: 'Documents you can read, and where answers come from.' },
    memory: { title: 'Memory', body: 'What it remembers about how you work. Yours to change or remove.' },
    approvals: { title: 'Approvals', body: 'Waiting on you before anything happens.' },
    activity: { title: 'Activity', body: 'Every run, what it cost and the sources it used. Last 30 days.' }
  },

  /**
   * The line under the tiles. It states the one fact that matters most about this deployment,
   * and it states it alone when the two counts are not known yet — a privacy claim is not
   * worth delaying, and a count that has not arrived must not be guessed at.
   */
  privacyLine: (fmt: Counting, bases: number | null, agents: number | null): string => {
    if (bases === null || agents === null) return 'nothing leaves your network'
    // Two counts in one line, each with its own plural form: English inflects the noun and
    // Portuguese inflects it at a different boundary (zero is singular there), so neither
    // number may be asked to decide for the other. Portuguese additionally has to agree the
    // participle of "shared with you" with *agente*, so its clause is plural-formed whole
    // rather than a noun with a fixed tail — same signature, same sentence, different seam.
    const baseWord = plural(fmt, bases, { one: 'knowledge base', other: 'knowledge bases' })
    const agentWord = plural(fmt, agents, { one: 'agent', other: 'agents' })
    return `${fmt.formatCount(bases)} ${baseWord} · ${fmt.formatCount(agents)} ${agentWord} shared with you · nothing leaves your network`
  }
}
